import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Shield, LayoutDashboard, Users, BarChart3, Activity,
  Building2, ArrowLeft, RefreshCw, Loader2, CheckCircle2, XCircle,
} from "lucide-react";
import AppShell from "../components/AppShell";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const TABS = [
  { id: "overview",  label: "Overview",   icon: LayoutDashboard },
  { id: "users",     label: "Users",      icon: Users },
  { id: "analytics", label: "Analytics",  icon: BarChart3 },
  { id: "ops",       label: "Ops Health", icon: Activity },
];

export default function SuperAdmin() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");
  const nav = useNavigate();

  if (!ready) return null;
  if (!user || user.id == null) return <Navigate to="/login" replace />;
  const role = (user.role || "").toLowerCase();
  if (role !== "super_admin") return <Navigate to="/projects" replace />;

  return (
    <AppShell>
      <div data-testid="superadmin-portal"
            style={{ display: "flex", minHeight: "calc(100vh - 60px)" }}>
        {/* Sidebar */}
        <aside data-testid="superadmin-sidebar"
                style={{ width: 240, flexShrink: 0,
                          borderRight: "1px solid var(--pf-border)",
                          background: "var(--pf-surface)",
                          padding: "20px 0" }}>
          <div style={{ padding: "0 18px 16px",
                         borderBottom: "1px solid var(--pf-border)",
                         marginBottom: 10,
                         display: "flex", alignItems: "center", gap: 10 }}>
            <Shield size={22} color="var(--pf-spark)"/>
            <div>
              <div className="pf-heading"
                    style={{ fontSize: 14, margin: 0 }}>Admin Portal</div>
              <div style={{ fontSize: 10, color: "var(--pf-text-muted)",
                              letterSpacing: 1, textTransform: "uppercase" }}>
                Capexara Process Industry · Platform Owner
              </div>
            </div>
          </div>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id}
                      onClick={() => setTab(t.id)}
                      data-testid={`superadmin-tab-${t.id}`}
                      data-active={active}
                      style={{ width: "100%", padding: "10px 18px",
                                display: "flex", alignItems: "center",
                                gap: 10, fontSize: 13,
                                background: active
                                  ? "var(--pf-surface-2)" : "transparent",
                                color: active
                                  ? "var(--pf-spark)" : "var(--pf-text)",
                                border: "none",
                                borderLeft: `3px solid ${active
                                  ? "var(--pf-spark)" : "transparent"}`,
                                cursor: "pointer", textAlign: "left" }}>
                <Icon size={14}/> {t.label}
              </button>
            );
          })}
          <div style={{ padding: "16px 18px 0" }}>
            <button className="btn btn-ghost"
                    onClick={() => nav("/projects")}
                    data-testid="superadmin-back-btn"
                    style={{ width: "100%", display: "inline-flex",
                              alignItems: "center", gap: 6,
                              justifyContent: "center", fontSize: 12 }}>
              <ArrowLeft size={12}/> Back to dashboard
            </button>
          </div>
        </aside>

        {/* Content */}
        <main style={{ flex: 1, padding: "28px clamp(20px, 3vw, 40px)",
                        overflowY: "auto" }}>
          {tab === "overview" && <OverviewTab/>}
          {tab === "users" && <UsersTab/>}
          {tab === "analytics" && <AnalyticsTab/>}
          {tab === "ops" && <OpsHealthTab/>}
        </main>
      </div>
    </AppShell>
  );
}


/* ─── OVERVIEW ─────────────────────────────────────────────────────── */
function OverviewTab() {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/superadmin/overview");
      setData(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="superadmin-overview">
      <SectionHeader title="Command centre"
        subtitle={data ? `Live snapshot of Capexara Process Industry across every tenant. As of ${new Date(data.as_of).toLocaleString()}.`
          : "Loading platform snapshot…"}
        onRefresh={load} busy={busy}/>
      {err && <ErrorBanner err={err}/>}

      {data && (
        <>
          <CardGrid>
            <BigCard label="Organisations" value={data.organisations.total}
                     sub={`${data.users.admins} admins on the platform`}
                     accent="#1E4E8C" testid="sa-card-orgs"/>
            <BigCard label="Users" value={data.users.total}
                     sub={`${data.users.active_30d} active 30d · ${data.users.verified} verified`}
                     accent="#34A853" testid="sa-card-users"/>
            <BigCard label="Projects" value={data.projects.total}
                     sub={`${data.sessions.last_30d} sessions last 30d`}
                     accent="#F26B1F" testid="sa-card-projects"/>
            <BigCard label="Sessions (total)" value={data.sessions.total}
                     sub={`${data.sessions.last_30d} in last 30 days`}
                     accent="#9C7BFB" testid="sa-card-sessions"/>
          </CardGrid>
        </>
      )}
    </div>
  );
}


/* ─── USERS ────────────────────────────────────────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");
  const [q, setQ]         = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/superadmin/users");
      setUsers(r.data?.users || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.toLowerCase();
    return users.filter(u =>
      (u.email||"").toLowerCase().includes(s) ||
      (u.name||"").toLowerCase().includes(s) ||
      (u.company_name||"").toLowerCase().includes(s));
  }, [users, q]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  const fmtMin = (m) => {
    if (!m) return "0 m";
    if (m < 60) return `${Math.round(m)} m`;
    const h = Math.floor(m / 60); const mm = Math.round(m % 60);
    return `${h} h ${mm} m`;
  };

  return (
    <div data-testid="superadmin-users">
      <SectionHeader title="Users"
        subtitle={`${users.length} total · login activity & total time spent`}
        onRefresh={load} busy={busy}/>
      {err && <ErrorBanner err={err}/>}

      <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="superadmin-users-search"
              className="pf-input"
              placeholder="Search by email, name or company…"
              style={{ marginBottom: 14, maxWidth: 460 }}/>

      <div style={{ background: "var(--pf-surface)",
                     border: "1px solid var(--pf-border)",
                     borderRadius: 6, overflow: "auto" }}>
        <table style={{ width: "100%", fontSize: 12,
                          borderCollapse: "collapse" }}
                data-testid="superadmin-users-table">
          <thead>
            <tr style={{ background: "var(--pf-surface-2)",
                          color: "var(--pf-text-muted)",
                          textAlign: "left" }}>
              <th style={th}>Email</th>
              <th style={th}>Name</th>
              <th style={th}>Company</th>
              <th style={th}>Org users</th>
              <th style={th}>Role</th>
              <th style={th}>Industry</th>
              <th style={th}>Verified</th>
              <th style={th}>Sessions</th>
              <th style={th}>Total time</th>
              <th style={th}>Last login</th>
              <th style={th}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} data-testid={`superadmin-user-row-${u.id}`}>
                <td style={td}><code>{u.email}</code></td>
                <td style={td}>{u.name || "—"}</td>
                <td style={td}>{u.company_name || "—"}</td>
                <td style={td}>
                  <span title={u.company_name
                    ? `${u.org_user_count} user(s) from ${u.company_name} on this platform`
                    : "No organisation set"}
                    style={{ display: "inline-flex",
                              alignItems: "center", gap: 4,
                              padding: "2px 8px", borderRadius: 10,
                              fontSize: 11, fontWeight: 600,
                              background: u.org_user_count > 1
                                ? "rgba(52, 168, 83, 0.18)"
                                : "var(--pf-surface-2)",
                              color: u.org_user_count > 1
                                ? "#34A853" : "var(--pf-text-muted)" }}>
                    <Building2 size={11}/> {u.org_user_count}
                  </span>
                </td>
                <td style={td}>
                  <span style={{ padding: "2px 6px", borderRadius: 3,
                                  fontSize: 10,
                                  background: u.role === "super_admin" ? "#7a1f1f"
                                    : u.role === "admin" ? "#1e4e8c" : "#3a3f4a",
                                  color: "#fff" }}>{u.role}</span>
                </td>
                <td style={td}>{u.industry || "—"}</td>
                <td style={td}>{u.email_verified
                  ? <CheckCircle2 size={12} color="#22c55e"/>
                  : <XCircle size={12} color="#dc2626"/>}</td>
                <td style={td}>{u.session_count}</td>
                <td style={td}>{fmtMin(u.total_minutes)}</td>
                <td style={td}>{fmt(u.last_login_at)}</td>
                <td style={td}>{fmt(u.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && !busy && (
              <tr><td colSpan={11} style={{ ...td, textAlign: "center",
                                              color: "var(--pf-text-muted)",
                                              fontStyle: "italic" }}>
                No users match the search.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─── ANALYTICS ───────────────────────────────────────────────────── */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/superadmin/analytics");
      setData(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const series = data?.series || [];
  const maxAny = Math.max(1, ...series.flatMap(s => [s.logins, s.signups, s.projects]));

  return (
    <div data-testid="superadmin-analytics">
      <SectionHeader title="Last 30 days · daily activity"
        subtitle={data ? `${data.totals.logins_30d} logins · ${data.totals.signups_30d} signups · ${data.totals.projects_30d} projects created`
          : "Loading analytics…"}
        onRefresh={load} busy={busy}/>
      {err && <ErrorBanner err={err}/>}

      {data && (
        <div style={{ background: "var(--pf-surface)",
                       border: "1px solid var(--pf-border)",
                       borderRadius: 6, padding: 18 }}>
          <Legend/>
          <div style={{ display: "flex", alignItems: "flex-end",
                          gap: 4, height: 260, marginTop: 8 }}>
            {series.map((d) => (
              <div key={d.date}
                    title={`${d.date} · ${d.logins} logins · ${d.signups} signups · ${d.projects} projects`}
                    style={{ flex: 1, display: "flex",
                              flexDirection: "column-reverse",
                              alignItems: "stretch", gap: 1 }}>
                <Bar value={d.logins}   max={maxAny} color="#F26B1F"/>
                <Bar value={d.signups}  max={maxAny} color="#34A853"/>
                <Bar value={d.projects} max={maxAny} color="#1E4E8C"/>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between",
                          fontSize: 9, color: "var(--pf-text-muted)",
                          marginTop: 6 }}>
            <span>{series[0]?.date}</span>
            <span>{series[Math.floor(series.length/2)]?.date}</span>
            <span>{series[series.length-1]?.date}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Bar({ value, max, color }) {
  const h = Math.max(0, Math.min(1, value / max)) * 80; // share each band gets
  return (
    <div style={{ height: `${h}px`, background: color,
                   borderRadius: "2px 2px 0 0",
                   minHeight: value > 0 ? 2 : 0 }}/>
  );
}

function Legend() {
  const items = [
    { label: "Logins",   color: "#F26B1F" },
    { label: "Signups",  color: "#34A853" },
    { label: "Projects", color: "#1E4E8C" },
  ];
  return (
    <div style={{ display: "flex", gap: 16, fontSize: 11,
                   color: "var(--pf-text-muted)" }}>
      {items.map(i => (
        <div key={i.label}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2,
                          background: i.color }}/>{i.label}
        </div>
      ))}
    </div>
  );
}


/* ─── OPS HEALTH ──────────────────────────────────────────────────── */
function OpsHealthTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/superadmin/ops-health");
      setData(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const checks = data?.checks || [];
  const summary = data?.summary || { ok: 0, down: 0, warn: 0, skipped: 0 };

  // Group checks by category for the service-health cards.
  const byCategory = useMemo(() => {
    const out = {};
    checks.forEach((c) => {
      if (!out[c.category]) out[c.category] = [];
      out[c.category].push(c);
    });
    return out;
  }, [checks]);

  return (
    <div data-testid="superadmin-ops">
      <SectionHeader title="Ops Health"
        subtitle="Live status of every external service the platform depends on"
        onRefresh={load} busy={busy}/>
      {err && <ErrorBanner err={err}/>}

      {data && (
        <>
          {/* Summary cards */}
          <CardGrid>
            <BigCard label="Services OK"
                     value={summary.ok}
                     sub={`${summary.total} services checked`}
                     accent="#34A853" testid="sa-ops-ok"/>
            <BigCard label="Services down"
                     value={summary.down}
                     sub="hard failures · need attention"
                     accent={summary.down ? "#dc2626" : "#34A853"}
                     testid="sa-ops-down"/>
            <BigCard label="Warnings"
                     value={summary.warn}
                     sub="reachable but auth / payload issue"
                     accent={summary.warn ? "#F59E0B" : "#34A853"}
                     testid="sa-ops-warn"/>
            <BigCard label="Not configured"
                     value={summary.skipped}
                     sub="env key not set · optional services"
                     accent="#6B7280" testid="sa-ops-skipped"/>
          </CardGrid>

          {/* Service cards, grouped by category */}
          <h3 className="pf-heading"
               style={{ fontSize: 14, margin: "22px 0 10px" }}>
            Service health
          </h3>
          {Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 18 }}>
              <div className="pf-overline"
                    style={{ fontSize: 10, color: "var(--pf-text-muted)",
                              marginBottom: 8, letterSpacing: 1.2 }}>
                {cat}
              </div>
              <div style={{ display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                              gap: 10 }}>
                {items.map((c) => (
                  <ServiceCard key={c.name} check={c}/>
                ))}
              </div>
            </div>
          ))}

          <h3 className="pf-heading"
               style={{ fontSize: 14, margin: "22px 0 10px" }}>
            Collection sizes
          </h3>
          <div style={{ background: "var(--pf-surface)",
                         border: "1px solid var(--pf-border)",
                         borderRadius: 6, overflow: "hidden" }}>
            <table style={{ width: "100%", fontSize: 12,
                              borderCollapse: "collapse" }}
                    data-testid="superadmin-ops-table">
              <thead>
                <tr style={{ background: "var(--pf-surface-2)",
                              color: "var(--pf-text-muted)",
                              textAlign: "left" }}>
                  <th style={th}>Collection</th>
                  <th style={th}>Document count</th>
                </tr>
              </thead>
              <tbody>
                {(data.collections || []).map(c => (
                  <tr key={c.name}>
                    <td style={td}><code>{c.name}</code></td>
                    <td style={td}>{c.count >= 0 ? c.count.toLocaleString()
                      : <span style={{ color: "var(--pf-err)" }}>unreadable</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


function ServiceCard({ check }) {
  const map = {
    ok:      { bar: "#34A853", chip: "OK",        ic: <CheckCircle2 size={14}/> },
    warn:    { bar: "#F59E0B", chip: "WARN",      ic: <CheckCircle2 size={14}/> },
    down:    { bar: "#dc2626", chip: "DOWN",      ic: <XCircle size={14}/> },
    skipped: { bar: "#6B7280", chip: "NOT SET",   ic: <XCircle size={14}/> },
  };
  const s = map[check.status] || map.skipped;
  return (
    <div data-testid={`sa-svc-${check.name.replace(/\s+/g, "-").toLowerCase()}`}
          style={{ background: "var(--pf-surface)",
                    border: "1px solid var(--pf-border)",
                    borderLeft: `4px solid ${s.bar}`,
                    borderRadius: 6, padding: "10px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                     justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8,
                       fontWeight: 600, fontSize: 13 }}>
          <span style={{ color: s.bar }}>{s.ic}</span>
          {check.name}
        </div>
        <span style={{ padding: "2px 8px", borderRadius: 10,
                        fontSize: 10, fontWeight: 700,
                        background: s.bar, color: "#fff",
                        letterSpacing: 0.4 }}>
          {s.chip}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--pf-text-muted)",
                     marginTop: 6, lineHeight: 1.4 }}>
        {check.detail || "—"}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between",
                     marginTop: 6, fontSize: 10,
                     color: "var(--pf-text-dim)" }}>
        <span>Latency</span>
        <span style={{ fontFamily: "var(--pf-mono, monospace)" }}>
          {check.latency_ms} ms
        </span>
      </div>
    </div>
  );
}


/* ─── Shared bits ─────────────────────────────────────────────────── */
function SectionHeader({ title, subtitle, onRefresh, busy }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start",
                   justifyContent: "space-between",
                   marginBottom: 18, gap: 16 }}>
      <div>
        <h1 className="pf-heading"
             style={{ fontSize: 26, margin: 0,
                        letterSpacing: "-0.02em" }}>{title}</h1>
        {subtitle && (
          <p style={{ color: "var(--pf-text-muted)",
                       fontSize: 13, margin: "4px 0 0" }}>{subtitle}</p>
        )}
      </div>
      <button onClick={onRefresh} disabled={busy}
              className="btn btn-ghost"
              data-testid="superadmin-refresh"
              style={{ display: "inline-flex",
                        alignItems: "center", gap: 6 }}>
        {busy ? <Loader2 size={12} className="pf-spin"/>
          : <RefreshCw size={12}/>} Refresh
      </button>
    </div>
  );
}

function CardGrid({ children }) {
  return (
    <div style={{ display: "grid",
                   gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                   gap: 14, marginBottom: 12 }}>
      {children}
    </div>
  );
}

function BigCard({ label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid}
          style={{ padding: "18px 20px",
                    background: "var(--pf-surface)",
                    border: "1px solid var(--pf-border)",
                    borderTop: `3px solid ${accent}`,
                    borderRadius: 6 }}>
      <div className="pf-overline"
            style={{ fontSize: 10, color: "var(--pf-text-muted)",
                      letterSpacing: 1.2, marginBottom: 8 }}>
        {label}
      </div>
      <div className="pf-heading"
            style={{ fontSize: 32, margin: 0,
                      letterSpacing: "-0.02em",
                      color: accent }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "var(--pf-text-muted)",
                       marginTop: 6 }}>{sub}</div>
      )}
    </div>
  );
}

function ErrorBanner({ err }) {
  return (
    <div className="pf-sev pf-sev-error"
          style={{ display: "block", padding: "8px 10px",
                    marginBottom: 12 }}
          data-testid="superadmin-error">{err}</div>
  );
}

const th = { padding: "8px 10px",
              borderBottom: "1px solid var(--pf-border)",
              fontWeight: 600, fontSize: 11,
              textTransform: "uppercase", letterSpacing: "0.04em" };
const td = { padding: "8px 10px",
              borderBottom: "1px solid var(--pf-border)" };
